/**
 * Frontend utility for validating coaching session/program times against amenity schedule slots
 */

/**
 * Convert time string (HH:MM) to minutes since midnight
 */
export function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to time string (HH:MM)
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Convert Date object to time string (HH:MM)
 */
export function dateToTimeString(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Check if a time aligns with slot boundaries relative to a reference start time
 */
export function isValidSlotBoundary(
  time: string,
  slotDurationMinutes: number,
  referenceStartTime: string = '00:00'
): boolean {
  const minutes = timeToMinutes(time);
  const referenceMinutes = timeToMinutes(referenceStartTime);
  const minutesFromReference = minutes - referenceMinutes;
  
  return minutesFromReference >= 0 && minutesFromReference % slotDurationMinutes === 0;
}

/**
 * Find the next valid slot boundary after the given time
 */
export function findNextSlotBoundary(
  time: string,
  slotDurationMinutes: number,
  referenceStartTime: string = '00:00'
): string {
  const minutes = timeToMinutes(time);
  const referenceMinutes = timeToMinutes(referenceStartTime);
  const minutesFromReference = minutes - referenceMinutes;
  
  // Round up to next slot boundary
  const slotsNeeded = Math.ceil(minutesFromReference / slotDurationMinutes);
  const nextBoundaryMinutes = referenceMinutes + (slotsNeeded * slotDurationMinutes);
  
  return minutesToTime(nextBoundaryMinutes);
}

/**
 * Find the previous valid slot boundary before the given time
 */
export function findPreviousSlotBoundary(
  time: string,
  slotDurationMinutes: number,
  referenceStartTime: string = '00:00'
): string {
  const minutes = timeToMinutes(time);
  const referenceMinutes = timeToMinutes(referenceStartTime);
  const minutesFromReference = minutes - referenceMinutes;
  
  // Round down to previous slot boundary
  const slotsNeeded = Math.floor(minutesFromReference / slotDurationMinutes);
  const prevBoundaryMinutes = referenceMinutes + (slotsNeeded * slotDurationMinutes);
  
  return minutesToTime(prevBoundaryMinutes);
}

/**
 * Validate that a booking time range aligns with slot boundaries
 */
export function validateBookingSlotAlignment(
  startTime: string,
  endTime: string,
  slotDurationMinutes: number,
  referenceStartTime: string = '00:00'
): { valid: boolean; error?: string; suggestion?: string } {
  // Check if start time aligns with slot boundary
  if (!isValidSlotBoundary(startTime, slotDurationMinutes, referenceStartTime)) {
    const nextStart = findNextSlotBoundary(startTime, slotDurationMinutes, referenceStartTime);
    const prevStart = findPreviousSlotBoundary(startTime, slotDurationMinutes, referenceStartTime);
    
    // Choose the closest boundary
    const nextDiff = timeToMinutes(nextStart) - timeToMinutes(startTime);
    const prevDiff = timeToMinutes(startTime) - timeToMinutes(prevStart);
    const suggestedStart = nextDiff <= prevDiff ? nextStart : prevStart;
    
    return {
      valid: false,
      error: `Start time ${startTime} does not align with ${slotDurationMinutes}-minute slot boundaries`,
      suggestion: suggestedStart
    };
  }
  
  // Check if end time aligns with slot boundary
  if (!isValidSlotBoundary(endTime, slotDurationMinutes, referenceStartTime)) {
    const nextEnd = findNextSlotBoundary(endTime, slotDurationMinutes, referenceStartTime);
    const prevEnd = findPreviousSlotBoundary(endTime, slotDurationMinutes, referenceStartTime);
    
    // Choose the closest boundary
    const nextDiff = timeToMinutes(nextEnd) - timeToMinutes(endTime);
    const prevDiff = timeToMinutes(endTime) - timeToMinutes(prevEnd);
    const suggestedEnd = nextDiff <= prevDiff ? nextEnd : prevEnd;
    
    return {
      valid: false,
      error: `End time ${endTime} does not align with ${slotDurationMinutes}-minute slot boundaries`,
      suggestion: suggestedEnd
    };
  }
  
  // Check if duration is a multiple of slot duration
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const durationMinutes = endMinutes - startMinutes;
  
  if (durationMinutes % slotDurationMinutes !== 0) {
    return {
      valid: false,
      error: `Booking duration must be a multiple of ${slotDurationMinutes} minutes`
    };
  }
  
  return { valid: true };
}

interface TimeSlot {
  startTime: string;
  endTime: string;
}

interface DaySchedule {
  dayOfWeek: number;
  slots: TimeSlot[];
}

interface AmenitySchedulePattern {
  frequency: 'daily' | 'weekly' | 'monthly';
  dailySchedule?: TimeSlot[];
  weeklySchedule?: DaySchedule[];
  effectiveStartDate: string;
  effectiveEndDate?: string;
}

interface AmenitySchedule {
  id: string;
  amenityId: string;
  name: string;
  schedulePattern: AmenitySchedulePattern;
  minBookingDurationMinutes: number;
  active: boolean;
}

export interface SlotAlignmentResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  scheduleDetails?: {
    amenityId: string;
    amenityName: string;
    scheduleName: string;
    minDuration: number;
    expectedSlots: string;
  }[];
  suggestions?: {
    amenityName: string;
    slotDuration: number;
    suggestedTime: string;
  }[];
}

/**
 * Validate that coaching session/program times align with all selected venue schedules
 * This now calls the backend API to use server-side validation logic
 */
export async function validateCoachingTimeSlotAlignment(
  venueIds: string[],
  venueSchedules: Map<string, AmenitySchedule[]>,
  venueNames: Map<string, string>,
  sessionDate: Date,
  startTime: string,
  endTime: string
): Promise<SlotAlignmentResult> {
  if (venueIds.length === 0) {
    return { valid: true, errors: [], warnings: [] };
  }

  try {
    // Import API service dynamically to avoid circular dependencies
    const { coachingSessionAPI } = await import('../services/api');
    
    // Call backend validation endpoint
    const response = await coachingSessionAPI.validateTimeSlots({
      venueIds,
      sessionDate: sessionDate.toISOString().split('T')[0], // YYYY-MM-DD
      startTime,
      endTime
    });

    return {
      valid: response.data.valid,
      errors: response.data.errors || [],
      warnings: response.data.warnings || [],
      scheduleDetails: response.data.scheduleDetails || []
    };
  } catch (error) {
    console.error('Error validating time slots:', error);
    return {
      valid: false,
      errors: ['Failed to validate time slots. Please try again.'],
      warnings: []
    };
  }
}

/**
 * Generate a user-friendly message from validation result
 */
export function formatSlotAlignmentMessage(result: SlotAlignmentResult): string {
  const parts: string[] = [];

  if (result.errors.length > 0 && result.suggestions && result.suggestions.length > 0) {
    parts.push('❌ Slot Alignment Issues:');
    result.suggestions.forEach(suggestion => {
      parts.push(
        `  • ${suggestion.amenityName} can be booked in ${suggestion.slotDuration}-minute slots. ` +
        `Session time can be changed to ${suggestion.suggestedTime} to align.`
      );
    });
  } else if (result.errors.length > 0) {
    parts.push('❌ Slot Alignment Issues:');
    result.errors.forEach(error => {
      parts.push(`  • ${error}: Session time does not align with available booking slots.`);
    });
  }

  if (result.warnings.length > 0) {
    parts.push('\n⚠️ Warnings:');
    result.warnings.forEach(warning => parts.push(`  • ${warning}`));
  }

  if (result.valid && result.scheduleDetails && result.scheduleDetails.length > 0) {
    parts.push('\n✅ Alignment Verified:');
    result.scheduleDetails.forEach(detail => {
      parts.push(`  • ${detail.amenityName}: ${detail.expectedSlots}`);
    });
  }

  if (parts.length === 0) {
    return 'Unable to validate slot alignment. Please check venue schedules.';
  }

  return parts.join('\n');
}
